import React, { useState, useEffect, useRef } from 'react';
import { Sword, ShoppingCart, Check, Menu, X, Gamepad2, Trophy, Vote, Home, ExternalLink, BookOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Logo } from '../ui/Logo';
import { Button } from '../ui/Button';
import { LanguageToggle } from '../ui/LanguageToggle';
import { links } from '../../constants/links';
import { copyToClipboard } from '../../utils/clipboard';
import { toast } from 'react-hot-toast';
import { Disclosure } from '@headlessui/react';
import { NavLink as RouterLink, useNavigate } from 'react-router-dom';

export function Navbar() {
  const { t, i18n } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const currentLanguage = i18n.language.startsWith('en') ? 'en' : 'id';

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    }

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  const handlePlayNow = async () => {
    const copied = await copyToClipboard(links.serverIp);
    if (copied) {
      toast.success(
        <div className="flex items-center whitespace-nowrap">
          <span className="font-medium tracking-wide">{links.serverIp}</span>
          <span className="text-gray-300 text-sm ml-3">{t('navbar.copied')}</span>
        </div>
      );
    } else {
      toast.error(
        <div className="flex items-center whitespace-nowrap">
          <span className="text-gray-300 text-sm">{t('navbar.copyError')}</span>
        </div>
      );
    }
  };

  const handleFeaturesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(`/${currentLanguage}`);
    setTimeout(() => {
      document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const navigationItems = (
    <>
      <RouterLink
        to={`/${currentLanguage}`}
        className={({ isActive }) =>
          `flex items-center space-x-2 text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium ${
            (isActive || location.pathname === '/' || location.pathname === '/en' || location.pathname === '/id') && 
            !location.pathname.split('/').filter(Boolean)[1] ? 'text-white bg-gray-700' : ''
          }`
        }
      >
        <Home className="w-5 h-5" />
        <span>{t('nav.home')}</span>
      </RouterLink>
      <RouterLink
        to={`/${currentLanguage}/leaderboard`}
        className={({ isActive }) =>
          `flex items-center space-x-2 text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium ${
            isActive && location.pathname.endsWith('/leaderboard') ? 'text-white bg-gray-700' : ''
          }`
        }
      >
        <Trophy className="w-5 h-5" />
        <span>{t('nav.leaderboard')}</span>
      </RouterLink>
      <RouterLink
        to={`/${currentLanguage}/vote`}
        className={({ isActive }) =>
          `flex items-center space-x-2 text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium ${
            isActive && location.pathname.endsWith('/vote') ? 'text-white bg-gray-700' : ''
          }`
        }
      >
        <Vote className="w-5 h-5" />
        <span>{t('navbar.vote')}</span>
      </RouterLink>
      <RouterLink
        to={`/${currentLanguage}/store`}
        className={({ isActive }) =>
          `flex items-center space-x-2 text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium ${
            (isActive || location.pathname.includes('/store/')) ? 'text-white bg-gray-700' : ''
          }`
        }
      >
        <ShoppingCart className="w-5 h-5" />
        <span>{t('navbar.store')}</span>
      </RouterLink>
      <a
        href={links.wiki}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center space-x-2 text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
      >
        <BookOpen className="w-5 h-5" />
        <span>{t('nav.wiki')}</span>
        <ExternalLink className="w-3 h-3 ml-1 text-gray-400" />
      </a>
    </>
  );

  const actionButtons = (
    <div className="flex items-center space-x-4">
      <Button variant="primary" onClick={handlePlayNow} className="text-sm px-3 py-1.5 flex items-center space-x-2 whitespace-nowrap justify-center">
        <Gamepad2 className="w-5 h-5" />
        <span>{t('navbar.playNow')}</span>
      </Button>
      <div className="hidden lg:block border-l border-gray-600 h-6" />
      <div className="hidden lg:block flex-shrink-0">
        <LanguageToggle />
      </div>
    </div>
  );

  return (
    <Disclosure as="nav" className="fixed w-[95%] z-50 mx-auto left-0 right-0 mt-4">
      {({ open }) => (
        <>
          <div className="bg-gray-900/90 backdrop-blur-sm rounded-xl shadow-xl border border-gray-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between h-16">
                {/* Left side - Logo and brand */}
                <div className="flex-shrink-0 flex items-center space-x-3">
                  <RouterLink to="/" className="flex items-center space-x-3 group">
                    <div className="transition-transform duration-300 group-hover:scale-110">
                      <Logo />
                    </div>
                    <span className="text-xl font-bold text-white hidden sm:inline bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                      Kaizen Network
                    </span>
                  </RouterLink>
                </div>
                
                {/* Center - Navigation Items */}
                <div className="hidden lg:flex flex-1 items-center justify-center space-x-1 px-4 min-w-0">
                  {navigationItems}
                </div>

                {/* Right side - Action buttons */}
                <div className="hidden lg:flex items-center space-x-4 flex-shrink-0">
                  {actionButtons}
                </div>

                {/* Mobile Menu Button */}
                <div className="lg:hidden flex items-center space-x-2">
                  <div className="flex-shrink-0">
                    <LanguageToggle />
                  </div>
                  <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20 transition-colors duration-200"
                  >
                    <span className="sr-only">Open main menu</span>
                    {isMobileMenuOpen ? (
                      <X className="block h-6 w-6" aria-hidden="true" />
                    ) : (
                      <Menu className="block h-6 w-6" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Navigation */}
          <div 
            ref={mobileMenuRef}
            className={`lg:hidden absolute right-0 w-64 transform transition-all duration-300 ease-in-out ${
              isMobileMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
            }`}
          >
            <div className="px-4 pt-2 pb-3 space-y-2 bg-gray-900/90 backdrop-blur-md shadow-2xl rounded-bl-xl border-l border-b border-white/5">
              <div className="flex flex-col">
                {navigationItems}
                <Button 
                  variant="primary" 
                  onClick={handlePlayNow} 
                  className="text-sm px-3 py-1.5 flex items-center space-x-2 whitespace-nowrap justify-center mt-2"
                >
                  <Gamepad2 className="w-5 h-5" />
                  <span>{t('navbar.playNow')}</span>
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </Disclosure>
  );
}

interface NavLinkProps {
  icon: React.ReactNode;
  text: string;
  href?: string;
  rightIcon?: React.ReactNode;
}

function NavLink({ icon, text, href = "#", rightIcon }: NavLinkProps) {
  return (
    <a 
      href={href}
      target={href.startsWith('http') ? '_blank' : undefined}
      rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
      className="group flex items-center text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 hover:bg-white/5"
    >
      <div className="flex items-center space-x-2">
        <div className="transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
          {icon}
        </div>
        <span>{text}</span>
      </div>
      {rightIcon && (
        <div className="ml-1 transition-transform duration-300 group-hover:translate-x-0.5">
          {rightIcon}
        </div>
      )}
    </a>
  );
}